import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Typography,
  Switch,
  FormControlLabel,
  Box,
  Divider,
  alpha,
  Paper,
  RadioGroup,
  Radio,
  FormLabel,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  AllInclusive as AllInclusiveIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/';

const AlertRuleDialog = ({ open, onClose, editingRule, bulkStrategies, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    bulk_strategy_id: '',
    is_enabled: true,
    play_sound: true,
    sound_file: 'alert-sound.mp3',
    active_time_enabled: false,
    active_start_time: '15:00',
    active_end_time: '16:59',
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (editingRule) {
      setFormData({
        name: editingRule.name,
        bulk_strategy_id: editingRule.bulk_strategy_id || '',
        is_enabled: editingRule.is_enabled,
        play_sound: editingRule.play_sound,
        sound_file: editingRule.sound_file || 'alert-sound.mp3',
        active_time_enabled: editingRule.active_time_enabled || false,
        active_start_time: editingRule.active_start_time || '15:00',
        active_end_time: editingRule.active_end_time || '16:59',
      });
    } else {
      setFormData({
        name: '',
        bulk_strategy_id: '',
        is_enabled: true,
        play_sound: true,
        sound_file: 'alert-sound.mp3',
        active_time_enabled: false,
        active_start_time: '15:00',
        active_end_time: '16:59',
      });
    }
    setError(null);
  }, [editingRule, open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validate
      if (!formData.name.trim()) {
        setError('Alert name is required');
        setSaving(false);
        return;
      }

      if (!formData.bulk_strategy_id) {
        setError('Bulk strategy is required');
        setSaving(false);
        return;
      }

      if (formData.active_time_enabled) {
        if (!formData.active_start_time || !formData.active_end_time) {
          setError('Start time and end time are required when time restriction is enabled');
          setSaving(false);
          return;
        }
      }

      const payload = {
        ...formData,
        rule_id: editingRule?.rule_id,
      };

      if (editingRule) {
        await axios.put(
          `${API_BASE_URL}/strategies/api/alerts/rules/${editingRule.rule_id}/update/`,
          payload
        );
      } else {
        await axios.post(`${API_BASE_URL}/strategies/api/alerts/rules/create/`, payload);
      }

      if (onSave) {
        await onSave();
      }
      onClose();
    } catch (err) {
      console.error('[ALERT] Save error:', err);
      setError(err.response?.data?.error || 'Failed to save alert rule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'rgba(15, 20, 25, 0.98)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 1.5,
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: 'rgba(26, 31, 46, 0.5)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        }}
      >
        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#fff' }}>
          {editingRule ? 'Edit Alert Rule' : 'Create Alert Rule'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: 'rgba(15, 20, 25, 0.8)', pt: 3 }}>
        <Stack spacing={2.5}>
          {error && (
            <Box
              sx={{
                p: 1.5,
                bgcolor: alpha('#ff4757', 0.1),
                border: `1px solid ${alpha('#ff4757', 0.3)}`,
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" sx={{ color: '#ff4757' }}>
                {error}
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            label="Alert Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g., NQ Strategy Completion Alert"
            required
          />

          <FormControl fullWidth required>
            <InputLabel>Bulk Strategy</InputLabel>
            <Select
              value={formData.bulk_strategy_id}
              onChange={(e) => handleChange('bulk_strategy_id', e.target.value)}
              label="Bulk Strategy"
            >
              <MenuItem value="">
                <em>Select Strategy</em>
              </MenuItem>
              {bulkStrategies.map((strategy) => (
                <MenuItem key={strategy.bulk_strategy_id} value={strategy.bulk_strategy_id}>
                  {strategy.bulk_strategy_id} ({strategy.symbol} - {strategy.timeframe})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider sx={{ borderColor: alpha('#fff', 0.1) }} />

          {/* TIME RESTRICTION SECTION */}
          <Paper
            sx={{
              p: 2.5,
              bgcolor: alpha('#764ba2', 0.05),
              border: `1px solid ${alpha('#764ba2', 0.2)}`,
              borderRadius: 1.5,
            }}
          >
            <FormControl component="fieldset">
              <FormLabel
                component="legend"
                sx={{
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '14px',
                  mb: 2,
                  '&.Mui-focused': {
                    color: '#fff',
                  },
                }}
              >
                Active Time Restriction
              </FormLabel>
              <RadioGroup
                value={formData.active_time_enabled ? 'restricted' : 'always'}
                onChange={(e) =>
                  handleChange('active_time_enabled', e.target.value === 'restricted')
                }
              >
                <FormControlLabel
                  value="always"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AllInclusiveIcon sx={{ fontSize: 20, color: '#00d4ff' }} />
                      <Box>
                        <Typography sx={{ fontWeight: 600, color: '#fff' }}>
                          24/7 Active
                        </Typography>
                        <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                          Alert triggers any time strategy reaches 100%
                        </Typography>
                      </Box>
                    </Box>
                  }
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor:
                      !formData.active_time_enabled
                        ? alpha('#00d4ff', 0.3)
                        : 'transparent',
                    bgcolor: !formData.active_time_enabled
                      ? alpha('#00d4ff', 0.05)
                      : 'transparent',
                    mb: 1,
                  }}
                />
                <FormControlLabel
                  value="restricted"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScheduleIcon sx={{ fontSize: 20, color: '#ffc107' }} />
                      <Box>
                        <Typography sx={{ fontWeight: 600, color: '#fff' }}>
                          Time-Restricted
                        </Typography>
                        <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                          Alert only triggers within specific time window
                        </Typography>
                      </Box>
                    </Box>
                  }
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: formData.active_time_enabled
                      ? alpha('#ffc107', 0.3)
                      : 'transparent',
                    bgcolor: formData.active_time_enabled
                      ? alpha('#ffc107', 0.05)
                      : 'transparent',
                  }}
                />
              </RadioGroup>

              {/* Time Selection */}
              {formData.active_time_enabled && (
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha('#fff', 0.1)}` }}>
                  <Typography
                    variant="body2"
                    sx={{ color: alpha('#fff', 0.7), mb: 1.5, fontWeight: 600 }}
                  >
                    Active Time Window
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                      fullWidth
                      type="time"
                      label="Start Time"
                      value={formData.active_start_time}
                      onChange={(e) => handleChange('active_start_time', e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        step: 300, // 5 min
                      }}
                    />
                    <Typography sx={{ color: alpha('#fff', 0.5) }}>to</Typography>
                    <TextField
                      fullWidth
                      type="time"
                      label="End Time"
                      value={formData.active_end_time}
                      onChange={(e) => handleChange('active_end_time', e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        step: 300, // 5 min
                      }}
                    />
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{ color: alpha('#ffc107', 0.8), mt: 1, display: 'block' }}
                  >
                    💡 Example: 15:00 - 16:59 for power hour trading
                  </Typography>
                </Box>
              )}
            </FormControl>
          </Paper>

          <Divider sx={{ borderColor: alpha('#fff', 0.1) }} />

          <FormControlLabel
            control={
              <Switch
                checked={formData.is_enabled}
                onChange={(e) => handleChange('is_enabled', e.target.checked)}
              />
            }
            label="Enable Alert"
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.play_sound}
                onChange={(e) => handleChange('play_sound', e.target.checked)}
              />
            }
            label="Play Sound"
          />
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          bgcolor: 'rgba(15, 20, 25, 0.6)',
          borderTop: '1px solid rgba(255, 255, 255, 0.06)',
          p: 2.5,
        }}
      >
        <Button onClick={onClose} sx={{ color: alpha('#fff', 0.5) }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!formData.name || !formData.bulk_strategy_id || saving}
          sx={{
            bgcolor: '#ffc107',
            color: '#000',
            fontWeight: 700,
            '&:hover': { opacity: 0.85 },
            '&:disabled': { opacity: 0.5 },
          }}
        >
          {saving ? 'Saving...' : editingRule ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlertRuleDialog;
 